import React, { useState, useEffect } from 'react';
import { X, Save, CreditCard, IndianRupee, Calendar, FileText, Hash } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { formatCurrency } from '../../lib/utils';
import { paymentService } from '../../lib/database/payments';
import { invoiceService } from '../../lib/database/invoices';
import { useAuth } from '../../hooks/useAuth';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../lib/stripe';

interface CreatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreatePaymentModal: React.FC<CreatePaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invoices, setInvoices] = useState<any[]>([]);

  // Form state
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'cash' | 'bank_transfer' | 'card' | 'upi' | 'cheque'>('cash');
  const [referenceId, setReferenceId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [showStripeForm, setShowStripeForm] = useState(false);

  // Load invoices on mount
  useEffect(() => {
    const loadInvoices = async () => {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices();
        if (invoicesData) {
          // Filter for unpaid invoices
          const unpaidInvoices = invoicesData.filter(invoice => invoice.status !== 'paid');
          setInvoices(unpaidInvoices);
        }
      } catch (error) {
        console.error('Error loading invoices:', error);
      }
    };

    if (isOpen) {
      loadInvoices();
    }
  }, [isOpen]);

  const selectedInvoice = invoices.find(inv => inv.id === invoiceId);
  const maxAmount = selectedInvoice ? Number(selectedInvoice.total) : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!invoiceId) {
        setError('Please select an invoice');
        return;
      }

      if (!amount || Number(amount) <= 0) {
        setError('Please enter a valid payment amount');
        return;
      }

      if (Number(amount) > maxAmount) {
        setError(`Payment amount cannot exceed invoice total of ${formatCurrency(maxAmount)}`);
        return;
      }

      if (!paymentDate) {
        setError('Please select a payment date');
        return;
      }

      // If card payment is selected, show Stripe form
      if (method === 'card') {
        setShowStripeForm(true);
        setLoading(false);
        return;
      }

      // Create payment
      const { data: payment, error: paymentError } = await paymentService.createPayment({
        invoice_id: invoiceId,
        amount: Number(amount),
        method,
        reference_id: referenceId.trim() || null,
        payment_date: paymentDate,
        notes: notes.trim() || null,
        created_by: user?.id || null,
      });

      if (paymentError || !payment) {
        setError('Failed to record payment: ' + (paymentError?.message || 'Unknown error'));
        return;
      }

      // Check if invoice is now fully paid and update status
      const { total: totalPaid } = await paymentService.getTotalPaymentsForInvoice(invoiceId);
      if (totalPaid >= maxAmount) {
        await invoiceService.markAsPaid(invoiceId);
      }

      setSuccess('Payment recorded successfully!');
      
      // Reset form
      setTimeout(() => {
        onSuccess();
        onClose();
        resetForm();
      }, 1500);

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStripePaymentSuccess = async () => {
    try {
      // Create payment record
      const { data: payment, error: paymentError } = await paymentService.createPayment({
        invoice_id: invoiceId,
        amount: Number(amount),
        method: 'card',
        reference_id: 'stripe_' + Date.now(),
        payment_date: paymentDate,
        notes: notes.trim() || 'Paid via Stripe',
        created_by: user?.id || null,
      });

      if (paymentError || !payment) {
        setError('Failed to record payment: ' + (paymentError?.message || 'Unknown error'));
        return;
      }

      // Check if invoice is now fully paid and update status
      const { total: totalPaid } = await paymentService.getTotalPaymentsForInvoice(invoiceId);
      if (totalPaid >= maxAmount) {
        await invoiceService.markAsPaid(invoiceId);
      }

      setSuccess('Payment processed successfully!');
      setShowStripeForm(false);
      
      // Reset form
      setTimeout(() => {
        onSuccess();
        onClose();
        resetForm();
      }, 1500);
    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    }
  };

  const resetForm = () => {
    setInvoiceId('');
    setAmount('');
    setMethod('cash');
    setReferenceId('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setError('');
    setSuccess('');
    setShowStripeForm(false);
  };

  if (!isOpen) return null;

  if (showStripeForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-success-50 to-success-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-success-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Process Card Payment</h2>
                <p className="text-gray-600">Invoice: {selectedInvoice?.invoice_number} - {formatCurrency(Number(amount))}</p>
              </div>
            </div>
            <button
              onClick={() => setShowStripeForm(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            <Elements stripe={stripePromise}>
              <div className="space-y-6">
                {error && <Alert type="error" message={error} />}
                {success && <Alert type="success" message={success} />}
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Invoice:</span>
                      <span className="font-medium">{selectedInvoice?.invoice_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Client:</span>
                      <span className="font-medium">{selectedInvoice?.client?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-bold text-success-600">{formatCurrency(Number(amount))}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    In a real implementation, this would connect to Stripe's payment form. For this demo, we'll simulate a successful card payment.
                  </p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="4242 4242 4242 4242"
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500 mt-1">For testing, use 4242 4242 4242 4242</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="MM/YY"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CVC</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="123"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowStripeForm(false)}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    icon={CreditCard}
                    loading={loading}
                    disabled={loading}
                    onClick={handleStripePaymentSuccess}
                    className="bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800"
                  >
                    Process Payment
                  </Button>
                </div>
              </div>
            </Elements>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-success-50 to-success-100">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-success-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Record Payment</h2>
              <p className="text-gray-600">Add a new payment transaction</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          {/* Invoice Selection */}
          <FormField label="Invoice" required>
            <select
              value={invoiceId}
              onChange={(e) => {
                setInvoiceId(e.target.value);
                setAmount(''); // Reset amount when invoice changes
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">Select an invoice to pay</option>
              {invoices.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_number} - {invoice.client?.name} - {formatCurrency(Number(invoice.total))}
                </option>
              ))}
            </select>
          </FormField>

          {/* Selected Invoice Info */}
          {selectedInvoice && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h4 className="text-blue-800 font-semibold mb-2">Invoice Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-600">Client:</span>
                  <p className="font-medium">{selectedInvoice.client?.name}</p>
                </div>
                <div>
                  <span className="text-blue-600">Total Amount:</span>
                  <p className="font-bold text-lg">{formatCurrency(Number(selectedInvoice.total))}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Payment Amount (₹)" icon={IndianRupee} required>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0.00"
                min="0.01"
                max={maxAmount}
                step="0.01"
                required
              />
            </FormField>

            <FormField label="Payment Method" icon={CreditCard} required>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as any)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Reference ID" icon={Hash} helpText="Transaction ID, cheque number, etc.">
              <input
                type="text"
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="TXN123456789"
              />
            </FormField>

            <FormField label="Payment Date" icon={Calendar} required>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </FormField>
          </div>

          {/* Notes */}
          <FormField label="Notes" icon={FileText} helpText="Optional payment notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Additional payment details..."
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Save}
              loading={loading}
              disabled={loading}
              className="bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800"
            >
              Record Payment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};